// Phase 30 — Driver Blocking Task Gate.
// Full-screen modal that blocks all interaction with the rest of the
// driver portal until the driver responds (accept/reject) to every
// pending task assigned to them. Polls every 30s.
//
// Phase 30b: when a new task arrives, vibrate the device + play a short
// alert tone using the Web Audio API (no asset required). The first user
// interaction in the page unlocks the audio context (Safari/iOS
// autoplay-policy compliant); after that, every newly-arriving task
// triggers a 2-pulse vibration + a 2-tone beep.

import { useEffect, useRef, useState } from "react";
import { fahrerApi } from "./lib/api";
import { AlertCircle, Check, X, Truck } from "lucide-react";
import { toast } from "sonner";
import { fmtDateTime } from "@/lib/format";

const POLL_MS = 30_000;

function vibrate(pattern) {
  try {
    if (typeof navigator !== "undefined" && navigator.vibrate) {
      navigator.vibrate(pattern);
    }
  } catch { /* ignore */ }
}

let _audioCtx = null;
function getAudioCtx() {
  if (typeof window === "undefined") return null;
  if (_audioCtx) return _audioCtx;
  const Ctor = window.AudioContext || window.webkitAudioContext;
  if (!Ctor) return null;
  _audioCtx = new Ctor();
  // Try to unlock the context as soon as possible
  if (_audioCtx.state === "suspended") {
    const unlock = () => {
      _audioCtx?.resume?.();
      window.removeEventListener("touchstart", unlock);
      window.removeEventListener("click", unlock);
      window.removeEventListener("keydown", unlock);
    };
    window.addEventListener("touchstart", unlock, { once: true });
    window.addEventListener("click", unlock, { once: true });
    window.addEventListener("keydown", unlock, { once: true });
  }
  return _audioCtx;
}

function beep() {
  try {
    const ctx = getAudioCtx();
    if (!ctx || ctx.state !== "running") return;
    const tones = [
      { f: 880, t: 0, d: 0.18 },  // A5
      { f: 660, t: 0.22, d: 0.20 }, // E5
    ];
    const now = ctx.currentTime;
    for (const tone of tones) {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = tone.f;
      gain.gain.value = 0.0001;
      gain.gain.linearRampToValueAtTime(0.6, now + tone.t + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + tone.t + tone.d);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(now + tone.t);
      osc.stop(now + tone.t + tone.d + 0.02);
    }
  } catch { /* ignore */ }
}

export default function BlockingTaskGate() {
  const [tasks, setTasks] = useState([]);
  const [busy, setBusy] = useState(false);
  const [rejectModal, setRejectModal] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const knownIdsRef = useRef(null); // null = first load (do not buzz on initial existing tasks)

  async function load() {
    try {
      const { data } = await fahrerApi.get("/fahrer/tasks/blocking");
      const list = data || [];
      const ids = new Set(list.map((t) => t.id));
      const known = knownIdsRef.current;
      if (known !== null) {
        // Only fire alert for genuinely NEW ids that we hadn't seen
        const fresh = [...ids].filter((id) => !known.has(id));
        if (fresh.length > 0) {
          vibrate([300, 120, 300, 120, 300]);
          beep();
        }
      }
      knownIdsRef.current = ids;
      setTasks(list);
    } catch { /* ignore */ }
  }

  useEffect(() => {
    // Pre-warm the audio context as early as possible
    getAudioCtx();
    load();
    const id = setInterval(load, POLL_MS);
    return () => clearInterval(id);
  }, []);

  if (tasks.length === 0) return null;
  const current = tasks[0];

  async function accept() {
    setBusy(true);
    try {
      await fahrerApi.post(`/fahrer/tasks/${current.id}/respond`, { response: "accepted", reason: "" });
      toast.success("Aufgabe angenommen");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler");
    } finally { setBusy(false); }
  }

  async function confirmReject() {
    if (rejectReason.trim().length < 5) {
      toast.error("Begründung min. 5 Zeichen");
      return;
    }
    setBusy(true);
    try {
      await fahrerApi.post(`/fahrer/tasks/${rejectModal.id}/respond`, { response: "rejected", reason: rejectReason.trim() });
      toast.success("Aufgabe abgelehnt");
      setRejectModal(null);
      setRejectReason("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler");
    } finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 bg-zinc-950/95 backdrop-blur-sm z-[100] flex items-center justify-center p-4 overflow-y-auto"
         data-testid="fahrer-blocking-gate">
      <div className="w-full max-w-md bg-zinc-900 border-2 border-amber-500 rounded-xl shadow-2xl">
        {/* Header */}
        <div className="px-5 py-4 border-b-2 border-amber-500/40 bg-gradient-to-r from-amber-500/20 to-amber-600/10">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-amber-400 animate-pulse"/>
            <h2 className="text-base font-bold text-amber-200">Neue Aufgabe — Annahme erforderlich</h2>
          </div>
          <p className="text-[11px] text-amber-200/80 mt-1">
            Du hast {tasks.length === 1 ? "eine offene Aufgabe" : `${tasks.length} offene Aufgaben`}. Bitte annehmen oder ablehnen, um weiterzumachen.
          </p>
        </div>

        {/* Task body */}
        <div className="p-5 space-y-3">
          <div className="flex items-start gap-2.5">
            <div className="w-9 h-9 rounded bg-amber-500/15 text-amber-300 border border-amber-500/40 flex items-center justify-center shrink-0">
              <Truck className="w-4 h-4"/>
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-base font-bold text-zinc-50" data-testid="gate-title">{current.title}</h3>
              <div className="flex items-center gap-2 text-[10px] text-zinc-400 mt-0.5">
                <span>Vergeben von <b className="text-zinc-200">{current.created_by || "—"}</b></span>
                <span>·</span>
                <span className="font-mono">{fmtDateTime(current.created_at)}</span>
              </div>
            </div>
          </div>

          {current.description && (
            <div className="bg-zinc-950 border border-zinc-800 rounded p-2.5">
              <div className="text-[10px] uppercase tracking-wide text-zinc-500 font-semibold mb-1">Beschreibung</div>
              <div className="text-sm text-zinc-200 whitespace-pre-wrap" data-testid="gate-description">{current.description}</div>
            </div>
          )}

          {current.notes && (
            <div className="bg-amber-500/5 border border-amber-500/30 rounded p-2.5">
              <div className="text-[10px] uppercase tracking-wide text-amber-300 font-semibold mb-1">Wichtige Hinweise</div>
              <div className="text-sm text-amber-100 whitespace-pre-wrap" data-testid="gate-notes">{current.notes}</div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-2 text-xs">
            <Stat label="Priorität" value={current.priority} />
            {current.due_date && <Stat label="Fällig" value={`${current.due_date}${current.due_time ? " " + current.due_time : ""}`} />}
          </div>

          {tasks.length > 1 && (
            <div className="text-[10px] text-amber-300/80 text-center pt-1">
              Weitere {tasks.length - 1} Aufgabe(n) folgen.
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="p-5 border-t border-zinc-800 grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => { setRejectModal(current); setRejectReason(""); }}
            disabled={busy}
            data-testid="gate-reject-btn"
            className="px-4 py-3 rounded bg-red-600 hover:bg-red-700 text-white font-bold text-sm inline-flex items-center justify-center gap-2 disabled:opacity-50">
            <X className="w-4 h-4"/> Ablehnen
          </button>
          <button
            type="button"
            onClick={accept}
            disabled={busy}
            data-testid="gate-accept-btn"
            className="px-4 py-3 rounded bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-sm inline-flex items-center justify-center gap-2 disabled:opacity-50">
            <Check className="w-4 h-4"/> Annehmen
          </button>
        </div>
      </div>

      {/* Reject reason modal */}
      {rejectModal && (
        <div className="fixed inset-0 bg-zinc-950/90 z-[110] flex items-center justify-center p-4" data-testid="gate-reject-modal">
          <div className="w-full max-w-md bg-zinc-900 border border-red-500/50 rounded-xl">
            <div className="px-5 py-4 border-b border-zinc-800 flex items-center gap-2">
              <X className="w-5 h-5 text-red-400"/>
              <h3 className="text-base font-bold text-zinc-100">Aufgabe ablehnen</h3>
            </div>
            <div className="p-5 space-y-3">
              <div className="text-sm text-zinc-300">
                Du lehnst die Aufgabe „<b>{rejectModal.title}</b>" ab.
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-wide text-zinc-400 font-semibold mb-1">
                  Grund (Pflicht, mind. 5 Zeichen)
                </label>
                <textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  rows={3}
                  autoFocus
                  data-testid="gate-reject-reason"
                  placeholder="z.B. bin gerade in einer anderen Tour, kann nicht abweichen"
                  className="w-full px-3 py-2 bg-zinc-950 border border-zinc-700 rounded text-sm text-zinc-100 focus:border-amber-500 focus:outline-none"
                />
              </div>
            </div>
            <div className="p-5 border-t border-zinc-800 grid grid-cols-2 gap-2">
              <button onClick={() => setRejectModal(null)} disabled={busy}
                      data-testid="gate-reject-cancel"
                      className="px-4 py-3 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-100 text-sm font-medium disabled:opacity-50">
                Abbrechen
              </button>
              <button onClick={confirmReject} disabled={busy || rejectReason.trim().length < 5}
                      data-testid="gate-reject-confirm"
                      className="px-4 py-3 rounded bg-red-600 hover:bg-red-700 disabled:bg-red-600/30 disabled:cursor-not-allowed text-white text-sm font-bold inline-flex items-center justify-center gap-2">
                <X className="w-4 h-4"/> Ablehnen bestätigen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-zinc-950 border border-zinc-800 rounded p-2">
      <div className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</div>
      <div className="text-sm text-zinc-100 capitalize">{value || "—"}</div>
    </div>
  );
}
